import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

function PrivacyPolicy() {
  const sections = [
    {
      title: 'Purpose',
      content:
        'This Privacy Policy outlines how Al Ibtekar Technical Solutions FZC collects, processes, stores, and protects personal data in compliance with the UAE Federal Decree-Law No. 45 of 2021 on the Protection of Personal Data (PDPL).',
    },
    {
      title: 'What Information We Collect',
      content: (
        <>
          Your information helps us:
          <ul className="list-disc pl-6 mt-2">
            <li>Full Name, Company Name, Email, Mobile Number</li>
            <li>Billing & Delivery Address</li>
            <li>Communications & Project Details</li>
            <li>Website usage data (via cookies or analytics tools)</li>
            <li>Technical specifications for projects or quotations</li>
          </ul>
        </>
      ),
    },
    {
      title: 'How We Use Your Information',
      content: (
        <>
          We process your data to:
          <ul className="list-disc pl-6 mt-2">
            <li>Respond to enquiries and offer customer support</li>
            <li>Fulfill your orders or service requests</li>
            <li>Process payments, invoices, and delivery logistics</li>
            <li>Improve service quality and website usability</li>
            <li>Share relevant updates (with your consent)</li>
          </ul>
        </>
      ),
    },
    {
      title: '4. Data Security',
      content: (
        <>
          We do not sell your data. We may share it with:
          <ul className="list-disc pl-6 mt-2">
            <li>UAE-based logistics or courier partners</li>
            <li>Authorized service providers under confidentiality agreements</li>
            <li>Government authorities when required by UAE law</li>
          </ul>
        </>
      ),
    },
    {
      title: 'Data Storage & Security',
      content: (
        <>
          <ul className="list-disc pl-6 mt-2">
            <li>
              Data is stored securely within the UAE or on trusted
              international servers with appropriate safeguards.
            </li>
            <li>
              We use encryption, firewall protection, and access control.
            </li>
            <li>
              Access to personal data is limited to authorized personnel only.
            </li>
          </ul>
        </>
      ),
    },
    {
      title: 'Your Rights (as per UAE PDPL)',
      content: (
        <>
          <ul className="list-disc pl-6 mt-2">
            <li>Access and correct your personal data</li>
            <li>Request deletion (unless legally restricted)</li>
            <li>Withdraw marketing consent</li>
            <li>Lodge complaints with the UAE Data Office if needed</li>
          </ul>
        </>
      ),
    },
    {
      title: 'Cookies & Analytics',
      content:
        'We use cookies to improve site experience. You can manage cookie preferences via your browser settings.',
    },
    {
      title: 'Policy Updates',
      content:
        'We may revise this Privacy Policy to reflect legal or service changes. Please check back periodically.',
    },
  ];

  return (
    <>
      {/* ================= SEO ================= */}
      <Helmet>
        <title>
          Privacy Policy | Al Ibtekar Technical Solutions FZC
        </title>

        <meta
          name="description"
          content="Read the Privacy Policy of Al Ibtekar Technical Solutions FZC to understand how personal data is collected, used, stored and protected in accordance with applicable UAE data protection requirements."
        />

        <meta
          name="robots"
          content="index, follow"
        />

        <link
          rel="canonical"
          href="https://saig.me/privacy-policy"
        />

        {/* Open Graph */}
        <meta
          property="og:title"
          content="Privacy Policy | Al Ibtekar Technical Solutions FZC"
        />

        <meta
          property="og:description"
          content="Learn how Al Ibtekar collects, uses, stores and protects personal information, including website usage data, project details and customer communications."
        />

        <meta
          property="og:url"
          content="https://saig.me/privacy-policy"
        />

        <meta
          property="og:type"
          content="website"
        />

        <meta
          property="og:site_name"
          content="Al Ibtekar"
        />

        {/* Twitter */}
        <meta
          name="twitter:card"
          content="summary"
        />

        <meta
          name="twitter:title"
          content="Privacy Policy | Al Ibtekar Technical Solutions FZC"
        />

        <meta
          name="twitter:description"
          content="Read Al Ibtekar's Privacy Policy covering personal data, cookies, data security, storage and user rights."
        />
      </Helmet>

      {/* ================= PAGE CONTENT ================= */}
      <section
        style={{
          background:
            'radial-gradient(104.59% 215.16% at 95% 10%, rgba(228, 74, 26, 0.16) 0%, rgba(228, 74, 26, 0) 29%), radial-gradient(104.59% 215.16% at 5% 90%, rgba(255, 27, 153, 0.15) 0%, rgba(255, 27, 153, 0) 25%)',
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          viewport={{ once: true }}
          className="max-w-5xl mx-auto font-kanit mt-[92px] px-4 py-10 text-gray-800"
        >
          {/* Main Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-6"
          >
            Privacy Policy
          </motion.h1>

          {/* Introduction */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            viewport={{ once: true }}
            className="mb-4"
          >
            We value your privacy. This policy outlines how we collect, use,
            and safeguard your personal information when you use our website
            or services.
          </motion.p>

          {/* Privacy Sections */}
          {sections.map((sec, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.4,
                delay: i * 0.1,
              }}
              viewport={{ once: true }}
            >
              <h2 className="text-xl font-semibold mt-6 mb-2">
                {sec.title}
              </h2>

              <div className="mb-4">
                {sec.content}
              </div>
            </motion.div>
          ))}

          {/* Last Updated */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.4,
              delay: 0.2,
            }}
            viewport={{ once: true }}
            className="mt-10 text-sm text-gray-500"
          >
            Last Updated: July 2025
          </motion.p>
        </motion.div>
      </section>
    </>
  );
}

export default PrivacyPolicy;